import { ArrowRightIcon } from '@synergy/theme-next';
import { Box, Divider, MenuItem, MenuItemProps, MenuList, styled } from '@mui/material';
import { Children, MouseEvent, ReactElement, useEffect, useState } from 'react';
import { PoolRow } from '@synergy/components-next';
import debounce from 'lodash.debounce';

import { Category } from '../../../../models/category';
import { categoriesSelector } from '../../../../store/categories-selectors';
import { fetchCategories } from '../../../../store/categories-actions';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';

const Column = styled(Box)(({ theme }) => ({
    maxWidth: theme.breakpoints.values.xl / 3,
    width: '30vw',
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 0px 9px rgba(0, 0, 0, 0.213853), 0px 2px 2px rgba(0, 0, 0, 0.144361)',
}));

const SlickDivider = styled(Divider)(() => ({
    margin: '0px !important',
}));

export interface CategoryTreePopupContentProps {
    parentId?: number;
    children?: ReactElement<MenuItemProps> | ReactElement<MenuItemProps>[];
    onSelect: (id: number) => void;
}

export const CategoryTreePopupContent = (props: CategoryTreePopupContentProps): ReactElement => {
    const dispatch = useAppDispatch();
    const categories = useAppSelector(state => categoriesSelector(state)(props.parentId));
    const [activeCategory, setActiveCategory] = useState<Category | null>(null);

    useEffect(() => {
        setActiveCategory(null);
        if (props.parentId && categories.length === 0) {
            dispatch(fetchCategories(props.parentId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, props.parentId]);

    const changeActiveCategory = debounce((category: Category | null) => {
        setActiveCategory(category?.hasChildren ? category : null);
    }, 250);

    const handleClick = (categoryId: number) => (e: MouseEvent) => {
        props.onSelect(categoryId);
        e.stopPropagation();
    };

    return (
        <>
            <Column>
                <MenuList sx={{ height: '100% ' }} onMouseLeave={() => changeActiveCategory.cancel()}>
                    {props.children && [
                        ...Children.map(props.children, (child, index) => (
                            <MenuItem key={-index} onMouseEnter={() => changeActiveCategory(null)} {...child.props} />
                        )),
                        <SlickDivider key="divider" />,
                    ]}
                    {categories.map(category => (
                        <MenuItem
                            key={category.id}
                            onMouseEnter={() => changeActiveCategory(category)}
                            onClick={handleClick(category.id)}
                            selected={activeCategory?.id === category.id}
                        >
                            <PoolRow justifyContent="space-between" sx={{ width: '100%' }}>
                                <Box sx={{ whiteSpace: 'normal' }}>{category.name}</Box>
                                {category.hasChildren && <ArrowRightIcon />}
                            </PoolRow>
                        </MenuItem>
                    ))}
                </MenuList>
            </Column>
            {activeCategory && <CategoryTreePopupContent parentId={activeCategory.id} onSelect={props.onSelect} />}
        </>
    );
};
